import type { IOrderDetail } from '@pu/mpp-ui/components/order-detail';
import dayjs from 'dayjs';
import { DEFAULT_CURRENCY } from 'core/constants/currency.constants';
import { ECurrencySymbols } from 'core/model/enums/currency.enum';
import { ECustomerSource } from 'core/model/enums/customer.enum';
import { EOrderCreatedStatus } from 'core/model/enums/orders.enum';
import { INewOrderParams, IOrderDisplay, IOrdersFilters, IOrderSuccess } from 'core/model/interfaces/order.interface';
import { IMetadata } from 'core/model/interfaces/product.interface';
import { hasProductsInGroupingRules } from 'core/store/checkout/checkout-state.utils';
import { formatDate } from 'shared/utils/format-dates.utils';
import {
  ICreateOrderRequestParams,
  IGetOrderDetailDTO,
  IGetOrderParams,
  IGetOrdersDTO,
  IOrderCreatedDTO,
  IOrderCreatedResponseDTO,
} from './orders.dto';

export const getShippingDate = (dates: Array<string>) => {
  if (!dates?.length) return '';
  return dates.reduce((accumulator, date) => (dayjs(date).diff(accumulator) > 0 ? date : accumulator), dates[0]);
};

export const mapOrdersDTOtoDisplayOrder = (response: IGetOrdersDTO): Array<IOrderDisplay> => {
  const orders: Array<IOrderDisplay> = response.orders.map(order => ({
    id: order.orderNumber,
    orderNumber: order.orderNumber,
    creationDate: order.createdAt,
    finalAmount: order.totals.final,
    status: order.status.name,
    sourceId: order.sourceId,
    hasDetail: order.hasDetail,
  }));
  return orders;
};

export const mapDatesToOrdersParams = (dates: IOrdersFilters): IGetOrderParams => ({
  startDate: dates.minDate && formatDate(dates.minDate),
  endDate: dates.maxDate && formatDate(dates.maxDate),
});

export const mapOrderToCreateOrderRequestParams = (params: INewOrderParams): ICreateOrderRequestParams => {
  const { customer, order } = params;
  const { source, legalConsent } = customer;
  const { paymentMethod, shipping, detail, address, agreements } = order;
  const { ids } = address;

  const payload: ICreateOrderRequestParams = {
    ...(!legalConsent && { hasAcceptedLegalConsent: agreements.legalConsent || undefined }),
    ...(!legalConsent && { hasAcceptedDataPrivacyConsent: agreements.dataPrivacyConsent || undefined }),
  };

  if (source === ECustomerSource.HYBRID) {
    if (hasProductsInGroupingRules(detail.alicorpCart)) {
      payload.own = {
        addressId: ids?.alicorp?.toString() ?? '',
        paymentMethodId: paymentMethod.alicorpCart.id || '',
        deliveryDate: shipping.deliveryDates.alicorpCart?.[0] || '',
      };
    }
    if (hasProductsInGroupingRules(detail.alliedCart)) {
      payload.thirdParty = {
        addressId: ids?.allied?.toString() ?? '',
        paymentMethodId: paymentMethod.alliedCart.id || '',
        deliveryDate: shipping.deliveryDates.alliedCart?.[0] || '',
      };
    }
  }

  if (source === ECustomerSource.DEX) {
    payload.own = {
      addressId: `${ids?.alicorp}`,
      paymentMethodId: paymentMethod.generalCart.id || '',
      deliveryDate: shipping.deliveryDates.generalCart?.[0] || '',
    };
  }

  if (source === ECustomerSource.APUDEX) {
    payload.thirdParty = {
      addressId: `${ids?.allied}`,
      paymentMethodId: paymentMethod.generalCart.id || '',
      deliveryDate: shipping.deliveryDates.generalCart?.[0] || '',
    };
  }

  return payload;
};

export const getGroupingRoules = (distributorDeliveryDates: Record<number, string>) => {
  const groupingRules = Object.keys(distributorDeliveryDates);

  return groupingRules.map(groupingRuleId => {
    const id = Number(groupingRuleId);
    return {
      id,
      deliveryDate: distributorDeliveryDates[id],
    };
  });
};

export const mapCreatedOrderDTOToDisplayOrder = (orderCreatedDTO: IOrderCreatedResponseDTO): IOrderSuccess => {
  const { orders } = orderCreatedDTO;

  const hasSameDeliveryDates = orders.every(
    (order, index) => index === 0 || order.deliveryDate === orders[index - 1].deliveryDate,
  );

  const hasSamePaymentMethods = orders.every(
    (order, index) => index === 0 || order.paymentMethodName === orders[index - 1].paymentMethodName,
  );

  const hasSameOrderInformation = hasSameDeliveryDates && hasSamePaymentMethods && orders.length > 0;

  const response: IOrderSuccess = {
    orders: orders.map(order => ({
      ...order,
      status: order.isAccepted ? EOrderCreatedStatus.Success : EOrderCreatedStatus.Rejected,
      sourceId: order.sourceId,
      paymentMethod: order.paymentMethodName,
      totalQuantityProduct: order.totalQuantityItems,
      products: order.items.map(item => ({
        brand: item.metadata.brand?.name || '',
        categoryName: item.metadata.category?.name || '',
        currency: DEFAULT_CURRENCY,
        description: item.description,
        promos: (item.promotions || []).map(promo => ({
          id: promo.externalId,
          name: promo.description,
          value: promo.discount || 0,
        })),
        eanCode: item.metadata.ean || '',
        image: item.image,
        name: item.productName,
        displayName: item.productName,
        presentation: item.presentation,
        price: item.unitPrice,
        hasDiscount: item.promotions?.length > 0,
        providerDisplayName: item.metadata.provider?.name || '',
        quantity: item.quantity,
        sku: item.externalProductId,
        subcategoryName: item.metadata.family?.name || '',
        distributorName: item.metadata.distributor?.name || '',
        metadata: {
          index: item.metadata?.index ?? 1,
          itemListName: item.metadata?.itemListName,
          origin: item.metadata?.origin,
        } as IMetadata,
      })),
      detail: mapOrderDetailResponseToOrderDetail(order),
      info: {
        address: order.address,
        deliveryDate: order.deliveryDate,
        paymentMethod: order.paymentMethodName,
      },
    })),
    info: null,
  };

  if (hasSameOrderInformation) {
    const { deliveryDate, paymentMethodName, address } = orders[0];
    response.info = { deliveryDate, paymentMethod: paymentMethodName, address };
  }

  return response;
};

export const mapOrderDetailResponseToOrderDetail = (
  orderDetailDto: IGetOrderDetailDTO | IOrderCreatedDTO,
): IOrderDetail => {
  const {
    address,
    deliveryDate,
    items,
    orderNumber,
    paymentMethodName,
    totalAmount,
    subtotalAmount,
    perceptionAmount,
    totalDiscountAmount,
    totalQuantityItems,
  } = orderDetailDto;

  return {
    id: `${orderNumber}`,
    totalCountProducts: totalQuantityItems,
    descriptionTotalDiscount: 'Total de promociones',
    totalDiscount: totalDiscountAmount,
    products: items.map(item => ({
      id: item.externalProductId,
      imageUrl: item.image,
      displayName: item.productName,
      currencySymbol: ECurrencySymbols.PEN,
      unitPrice: item.originalUnitPrice,
      quantity: item.quantity,
      finalPrice: Number(item.totalAmount),
      promos: item.promotions.map(promo => ({
        description: promo.description,
        totalValue: Number(promo.discount),
      })),
      presentationType: item.unitMeasureId,
      unitMeasureName: item.unitMeasureName,
      unitMeasurePluralName: item.unitMeasurePluralName,
    })),
    currencySymbol: ECurrencySymbols.PEN,
    subTotal: subtotalAmount,
    perception: perceptionAmount || 0,
    finalTotal: totalAmount,
    paymentType: paymentMethodName,
    deliveryDate: dayjs(deliveryDate).format('DD/MM/YY'),
    address,
  };
};
